import React from 'react'

import { Delegate } from '../interfaces/OrganizationDto'
import { Salenote } from '../interfaces/Salenote'
import UserData from '../interfaces/UserData'
import { makeImage } from '../utils/makeImage'
import { fechaIsoToDD_MM_YYYY, formatCurrency, formatWithThousandSep, insertAfterFirstToken } from '../utils/utils'
import NumeroALetras from '../utils/numeroAletras'
import { vehicleTypeTranslateEx } from '../utils/vehicleTypeTranslate'

import { PrintableCard } from './PrintableCard'

interface SalenoteContractTemplateProps {
  salenote: Salenote
  userData: UserData
  sellerName: string
  sellerRut: string
  sellerAddr: string
  delegates: Delegate[]
  showRepresentatives: boolean
  personeria1: string
  personeria2: string
  representadoPorSpan: React.ReactNode
  representado2PorSpan: React.ReactNode
  vehPlateSpan: React.ReactNode
  overrideAppraisal?: number
}

export const SalenoteContractTemplate: React.FC<SalenoteContractTemplateProps> = ({
  salenote,
  userData,
  sellerName,
  sellerRut,
  sellerAddr,
  delegates,
  showRepresentatives,
  personeria1,
  personeria2,
  representadoPorSpan,
  representado2PorSpan,
  vehPlateSpan,
  overrideAppraisal,
}) => {
  const urlImage = userData?.organization?.pictures.logotype
  const sellerSpan = <span className="font-bold underline">{sellerName}</span>
  const sellerRutSpan = <span className="font-bold underline">{sellerRut}</span>
  const sellerAddrSpan = <span className="font-bold underline">{sellerAddr}</span>

  const sellerArticleDateSpan = (
    <span className="font-bold underline !hyphens-none">
      {' '}
      {fechaIsoToDD_MM_YYYY(userData?.organization?.articles?.date)}
    </span>
  )
  const sellerArticleNotarySpan = (
    <span className="font-bold underline">{userData?.organization?.articles?.notary}</span>
  )
  const sellerArticleMunicipalitySpan = (
    <span className="font-bold underline">{userData?.organization?.articles?.municipality}</span>
  )

  const clientName = `${salenote?.body.client.firstname} ${salenote?.body.client.lastname}`
  const clientRut = salenote?.body.client.nin
  const clientRutSpan = <span className="font-bold underline">{clientRut}</span>

  const c1 = salenote?.body.client.address
  const c2 = (salenote?.body.client.streetNumber ?? '').toString()
  const c3 = salenote?.body.client.addressComponents ?? ''
  const fullAddress = insertAfterFirstToken(c1, c2, c3)
  const clientAddr = fullAddress

  const clientNameSpan = <span className="font-bold underline">{clientName}</span>
  const clientAddrSpan = <span className="font-bold underline">{clientAddr}</span>

  let buyerText = (
    <>
      y {clientNameSpan} RUT {clientRutSpan} domiciliado para estos efectos en {clientAddrSpan}, como el “Comprador“,
    </>
  )

  let adquiereParaText = (
    <>
      quien compra, acepta y adquiere para sí un vehículo tipo
    </>
  )

  if (salenote?.compraPara?.enabled) {
    const compraParaOwnerSpan = <span className="font-bold underline">{salenote.compraPara.ownerName}</span>
    const compraParaRutSpan = <span className="font-bold underline">{salenote.compraPara.ownerRut}</span>
    
    buyerText = (
      <>
        y {clientNameSpan} RUT {clientRutSpan} domiciliado para estos efectos en {clientAddrSpan}, como el “Comprador“, quien compra para {compraParaOwnerSpan} RUT {compraParaRutSpan},
      </>
    )

    adquiereParaText = (
      <>
        quien compra, acepta y adquiere para {compraParaOwnerSpan} RUT {compraParaRutSpan} un vehículo tipo
      </>
    )
  }

  // Parts Div
  const partsDiv = (
    <div className="mb-1">
      Entre {sellerSpan} RUT {sellerRutSpan} domiciliado para estos efectos en {sellerAddrSpan}
      {representadoPorSpan}
      {representado2PorSpan} como el “Vendedor”, {buyerText} y conjuntamente, las “Partes”; se ha convenido el siguiente contrato de
      compraventa, en adelante, el “Contrato”:
    </div>
  )

  const personeria2Aux = personeria2 ? ` y ${personeria2}` : ''
  const personeriaDiv = (
    <>
      <span className="font-bold">SÉPTIMO.-</span> La personería de{' '}
      <span className="font-bold underline">
        {personeria1}
        {personeria2Aux}
      </span>
      , para representar a {sellerSpan} , consta de la escritura pública de fecha {sellerArticleDateSpan} , otorgada en
      la notaría de {sellerArticleMunicipalitySpan} de don (doña) {sellerArticleNotarySpan} , Notario Público.
    </>
  )

  const vehTipo = vehicleTypeTranslateEx(salenote?.body.vehicle.type ?? '')
  const vehTipoSpan = <span className="font-bold underline">{vehTipo}</span>
  const vehColor = salenote?.body.vehicle.color
  const vehColorSpan = <span className="font-bold underline">{vehColor}</span>
  const vehChassis = salenote?.body.vehicle.chassisId
  const vehChassisSpan = <span className="font-bold underline">{vehChassis}</span>
  const vehBrand = salenote?.body.vehicle.brand
  const vehBrandSpan = <span className="font-bold underline">{vehBrand}</span>
  const vehModel = `${salenote?.body.vehicle.model} ${salenote?.body.vehicle.version}`
  const vehModelSpan = <span className="font-bold underline">{vehModel}</span>
  const vehYear = salenote?.body.vehicle.year
  const vehYearSpan = <span className="font-bold underline">{vehYear}</span>
  const vehMotor = salenote?.body.vehicle.engineId
  const vehMotorSpan = <span className="font-bold underline">{vehMotor}</span>
  const vehKm = formatWithThousandSep(salenote?.body.vehicle.odometer ?? 0)
  const vehKmSpan = <span className="font-bold underline">{vehKm}</span>
  const price = formatCurrency(salenote?.price ?? 0)
  const totalInWords = NumeroALetras(salenote?.price ?? 0)
  const priceSpan = (
    <span className="font-bold underline">
      {price} ({totalInWords})
    </span>
  )
  const appraisalVal = overrideAppraisal ?? salenote?.body.vehicle.appraisal ?? 0
  const appraisal = formatCurrency(appraisalVal)
  const appraisalInWords = NumeroALetras(appraisalVal)
  const appraisalSpan = (
    <span className="font-bold  underline">
      {appraisal} ({appraisalInWords})
    </span>
  )

  const today = new Date()
  const day = today.getDate()
  const daySpan = <span className="font-bold underline">{day}</span>
  const monthName = today.toLocaleString('es-ES', { month: 'long' })
  const monthNameSpan = <span className="font-bold underline">{monthName}</span>
  const year = today.getFullYear()
  const yearSpan = <span className="font-bold underline">{year}</span>

  let paymentAmount = price
  let paymentAmount2Span = <span></span>
  const methodOfPayment = 'TRANSFERENCIA ELECTRÓNICA'
  const credito = salenote?.credit

  if (credito) {
    paymentAmount = formatCurrency((salenote?.price ?? 0) - credito.amount)
    const paymentAmount2 = ` y ${formatCurrency(credito.amount)}`
    paymentAmount2Span = (
      <span>
        <span className="font-bold underline">{paymentAmount2}</span> con un{' '}
        <span className="font-bold underline">CRÉDITO</span>
      </span>
    )
  }
  const paymentAmountSpan = <span className="font-bold underline">{paymentAmount}</span>
  const methodOfPaymentSpan = <span className="font-bold underline">{methodOfPayment}</span>
  const signatureLine = '____________________________'

  return (
    <PrintableCard>
      <div>
        <div className="flex-shrink-0">
          <img src={makeImage(urlImage ?? '')} alt="Logo" className="h-10" />
        </div>
      </div>

      <div className="flex justify-center text-lg font-bold m-3">CONTRATO DE COMPRAVENTA DE VEHÍCULO</div>

      <div className="mb-1">
        En SANTIAGO, REPÚBLICA DE CHILE, a {daySpan} de {monthNameSpan} de {yearSpan}.
      </div>
      <div className="text-justify hyphens-auto">
        {partsDiv}
        <div className="mb-1">
          <span className="font-bold">PRIMERO.-</span> {sellerSpan} vende, cede y transfiere a {clientNameSpan} {adquiereParaText} {vehTipoSpan} Color {vehColorSpan} chasis N°{' '}
          {vehChassisSpan} marca {vehBrandSpan} modelo {vehModelSpan} año {vehYearSpan} con motor N° {vehMotorSpan} y{' '}
          {vehKmSpan} kilómetros, actual Patente e Inscripción en el Registro Nacional de Vehículos Motorizados del
          Registro Civil N° {vehPlateSpan} en el estado en que actualmente se encuentra y que el Comprador conoce
          cabalmente.
        </div>
        <div className="mb-1">
          <span className="font-bold">SEGUNDO.-</span> El precio de venta es la suma de {priceSpan} que el Comprador
          paga de la siguiente forma: {paymentAmountSpan} con {methodOfPaymentSpan}
          {paymentAmount2Span} y que el Vendedor declara recibir a su entera satisfacción. La tasación fiscal es de{' '}
          {appraisalSpan}.
        </div>
        <div className="mb-1">
          <span className="font-bold">TERCERO.-</span> Declara el Vendedor bajo juramento que el vehículo que vende no
          tiene deudas ni gravámenes de ninguna especie: como prenda, embargo o prohibición de enajenar. El Comprador
          declara, por su parte, que este hecho es causa determinante de esta compraventa y que ha estudiado debidamente
          el dominio vigente del vehículo que compra. Ambas Partes liberan al Notario que autoriza de toda
          responsabilidad al respecto.
        </div>
        <div className="mb-1">
          <span className="font-bold">CUARTO.-</span> Declara el Comprador que ha revisado detenidamente: a) el vehículo
          objeto de este Contrato, verificando que el VIN, el número de chasis y el número de motor coinciden con los
          registros oficiales del vehículo; y b) la cédula de identidad del Vendedor, verificando que esta es auténtica
          y no presenta adulteraciones. Por lo tanto, declara recibir el vehículo a su entera satisfacción, expresa no
          tener cargo alguno ni objeción que formular y renuncia expresamente a las acciones de saneamiento por vicios
          redhibitorios establecido en el párrafo 8 del Título XXIII del Libro IV del Código Civil.
        </div>
        <div className="mb-1">
          <span className="font-bold">QUINTO.-</span> El Comprador, acepta el presente vehículo en su situación y
          responsabilidad de multas, la cual consta en el Certificado de Multas de Tránsito Impagas del Servicio de
          Registro Civil e Identificación.
        </div>
        <div className="mb-1">
          <span className="font-bold">SEXTO.-</span> El Comprador otorga mandato comercial e irrevocable y oneroso al
          notario autorizante, para efectuar la inscripción de este Contrato en el Registro Nacional de Vehículos
          Motorizados.
        </div>
        {showRepresentatives && <div>{personeriaDiv}</div>}
      </div>
      <div className="mt-12 flex justify-between">
        <div className="flex">
          <div className="flex flex-col items-center mr-10">
            <div>{signatureLine}</div>
            <div>Firma del Vendedor</div>
            <div>RUT: {sellerRut}</div>
            {showRepresentatives && <div>PP: {delegates[0].nin}</div>}
          </div>
          {showRepresentatives && delegates.length > 1 && (
            <div className="flex flex-col items-center">
              <div>{signatureLine}</div>
              <div>Firma del Vendedor</div>
              <div>RUT: {sellerRut}</div>

              <div>PP: {delegates[1].nin}</div>
            </div>
          )}
        </div>
        <div className="flex flex-col items-center mr-4">
          <div>{signatureLine}</div>
          <div>Firma del Comprador</div>
          <div>RUT: {clientRut}</div>
        </div>
      </div>

      <div className="mt-8 mb-8 text-xs">
        El derecho Municipal correspondiente al 1,5% se enteró en el
        Banco__________________________________________________
        <div> con fecha ______________________ Valor Pagado ____________________</div>
      </div>
    </PrintableCard>
  )
}
